import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Banknote, CreditCard, ArrowRightLeft, Loader2, CheckCircle, Gift } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const METHODS = [
  { value: 'efectivo', label: 'Efectivo', icon: Banknote },
  { value: 'tarjeta', label: 'Tarjeta', icon: CreditCard },
  { value: 'transferencia', label: 'Transfer', icon: ArrowRightLeft },
];

export default function CheckoutModal({
  open,
  onOpenChange,
  total,
  discount,
  setDiscount,
  pointsToRedeem,
  setPointsToRedeem,
  paymentMethod,
  setPaymentMethod,
  hasReward,
  availablePoints,
  onConfirm,
  loading,
}) {
  const finalTotal = Math.max(0, total - (parseFloat(discount) || 0) - (parseInt(pointsToRedeem) || 0));
  const coveredByPoints = finalTotal === 0 && (hasReward || (parseInt(pointsToRedeem) || 0) > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{coveredByPoints ? 'Confirmar Canje' : 'Procesar Pago'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="text-center py-4 bg-slate-50 rounded-xl">
            <div className="text-sm text-slate-500">Total a Pagar</div>
            <div
              className={`text-4xl font-bold tabular-nums ${coveredByPoints ? 'text-emerald-600' : 'text-blue-600'}`}
              data-testid="checkout-final-total"
            >
              {formatCurrency(finalTotal)}
            </div>
          </div>

          {coveredByPoints ? (
            <div
              className="p-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 flex items-center gap-3"
              data-testid="checkout-covered-by-points"
            >
              <Gift className="w-6 h-6 text-emerald-600 shrink-0" />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold">Pagado con puntos</div>
                <div>No se requiere cobro en pesos.</div>
              </div>
            </div>
          ) : (
            <div>
              <Label>Método de Pago</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {METHODS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={`p-4 border-2 rounded-xl flex flex-col items-center gap-2 transition-all ${
                      paymentMethod === value ? 'border-blue-600 bg-blue-50' : 'border-slate-200'
                    }`}
                    onClick={() => setPaymentMethod(value)}
                    data-testid={`payment-${value}`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="discount">Descuento</Label>
              <Input
                id="discount"
                type="number"
                min="0"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="mt-1"
                data-testid="checkout-discount"
              />
            </div>
            <div>
              <Label htmlFor="points">Usar Puntos</Label>
              <Input
                id="points"
                type="number"
                min="0"
                max={availablePoints}
                value={pointsToRedeem}
                onChange={(e) => setPointsToRedeem(e.target.value)}
                className="mt-1"
                data-testid="checkout-points"
              />
              <div className="text-xs text-slate-500 mt-1">
                Disponibles: {availablePoints}
              </div>
            </div>
          </div>

          <Button
            className="pos-btn-success"
            onClick={() => onConfirm(coveredByPoints ? 'puntos' : paymentMethod)}
            disabled={loading}
            data-testid="confirm-payment-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                {coveredByPoints ? 'Confirmar Canje' : 'Confirmar Pago'}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
